/* eslint-disable import/named */
import { type ReactElement, useRef, useState } from "react";
import { UseFormRegister, RegisterOptions } from "react-hook-form";
import {
  ParsedCountry,
  PhoneInput as RPhoneInput,
  PhoneInputRefType,
} from "react-international-phone";
import "react-international-phone/style.css";
import Text from "../../atoms/Text";
import isPhoneValid, {
  validatePhoneNumber,
  PhoneValidationMessages,
} from "./phoneValidation";
import * as s from "./styles";

export interface Props {
  defaultCountry?: string;
  defaultValue?: string;
  label?: string;
  labelInfoIcon?: React.ReactElement;
  name: string;
  placeholder?: string;
  required?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  register?: UseFormRegister<any>;
  validationOptions?: Partial<RegisterOptions>;
  validationMessages: PhoneValidationMessages;
  onChange?: (
    phone: string,
    meta: { country: ParsedCountry; inputValue: string },
  ) => void;
}

export default function PhoneInput({
  defaultCountry = "us",
  defaultValue,
  label,
  labelInfoIcon,
  name,
  placeholder,
  required = false,
  hasError = false,
  errorMessage,
  helperText,
  disabled = false,
  readOnly = false,
  register,
  validationOptions,
  validationMessages,
  onChange,
}: Props): ReactElement {
  const [phone, setPhone] = useState<string | undefined>(defaultValue);
  const [isDirty, setIsDirty] = useState(false);
  const ref = useRef<PhoneInputRefType>(null);

  const isInternallyValid = isDirty && isPhoneValid(phone);
  const hasInternalError = isDirty && !isInternallyValid;
  const showValidation = hasError || hasInternalError;

  const handlePhoneChange = (
    phone: string,
    meta: { country: ParsedCountry; inputValue: string },
  ) => {
    setIsDirty(phone.length > 0);
    setPhone(phone);
    onChange?.(phone, meta);
  };

  const handleLabelClick = () => ref.current?.focus();

  const handleLabelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      ref.current?.focus();
    }
  };

  const createValidator = () => {
    if (validationOptions?.validate) {
      return validationOptions.validate;
    }
    return (value: string) =>
      validatePhoneNumber(value, required, validationMessages);
  };

  const registrationProps = register
    ? register(name, {
        ...validationOptions,
        required: validationOptions?.required,
        validate: createValidator(),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const phoneValue = e.target.value;
          handlePhoneChange(phoneValue, {
            country: { iso2: defaultCountry } as ParsedCountry,
            inputValue: phoneValue,
          });
          validationOptions?.onChange?.(e);
        },
      })
    : {};

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label
          htmlFor={name}
          onClick={handleLabelClick}
          onKeyDown={handleLabelKeyDown}
          className={`flex items-center cursor-pointer ml-2 text-white ${
            disabled ? "opacity-50" : ""
          }`}
        >
          <Text weight="bold" color="white">
            {label}
          </Text>
          {required && <span className="ml-0.5 text-white">*</span>}
          {labelInfoIcon && <span className="ml-0.5">{labelInfoIcon}</span>}
        </label>
      )}

      <div className={s.phoneInputWrapper}>
        <RPhoneInput
          disableFormatting
          showDisabledDialCodeAndPrefix
          defaultCountry={defaultCountry}
          inputProps={{
            id: name,
            placeholder,
            disabled,
            readOnly,
            ...registrationProps,
          }}
          name={name}
          onChange={handlePhoneChange}
          ref={ref}
          value={phone}
          inputClassName={s.input(showValidation)}
          countrySelectorStyleProps={{
            buttonClassName: s.countrySelectorButton(showValidation),
            dropdownStyleProps: {
              listItemClassName: s.countrySelectorListItem,
              listItemCountryNameClassName:
                s.countrySelectorListItemCountryName,
              listItemDialCodeClassName: s.countrySelectorListItemDialCode,
            },
          }}
          dialCodePreviewStyleProps={{
            className: s.dialCodePreview(showValidation),
          }}
        />
      </div>

      {helperText && (
        <Text size="sm" color="secondary">
          {helperText}
        </Text>
      )}

      {showValidation && (
        <Text size="sm" color="error">
          {errorMessage}
        </Text>
      )}
    </div>
  );
}
